<script>
  import MapView from "./MapView.svelte"
  import StorageUnitRowPanel from "./StorageUnitRowPanel.svelte"
  import UnassignedStorageUnitsPanel from "./UnassignedStorageUnitsPanel.svelte"
  import { initMapSettings, initUpdateMapSettings, throttleScrollDelta } from "./utils"
  import { onMount, setContext } from "svelte"
  import { writable } from "svelte/store"

  export let siteMapId

  let debugStore = { enabled: false }

  const mapSettings = initMapSettings({
    isEditing: true,
    isSnapToGridEnabled: true,
    viewWidth: 100,
  })

  const updateMapSettings = initUpdateMapSettings(mapSettings)

  function updateDebugStore(options) {
    debugStore = { ...debugStore, ...options }
  }

  setContext("mapSettings", mapSettings)
  setContext("updateDebugStore", updateDebugStore)

  onMount(load)

  let status = "loading"
  let loadError
  let storageUnitTypes
  let storageUnitTypesById
  let storageUnitRows
  let storageUnitRowsById
  let unassignedStorageUnits
  let mapHeight
  let mapBackgroundImageUrl
  let viewXCenter = 50
  let viewYCenter = 50

  let selectedStorageUnitRowId

  const minViewWidth = 50

  async function load() {
    try {
      const response = await fetch(`/company_admin/site_maps/${siteMapId}`, {
        headers: { Accept: "application/json" },
      })
      const siteMap = await response.json()
      storageUnitTypes = siteMap.storageUnitTypes
      for (const storageUnitType of storageUnitTypes) {
        storageUnitType.shape.width = Math.max(...storageUnitType.shape.map((p) => p[0]))
        storageUnitType.shape.height = Math.max(...storageUnitType.shape.map((p) => p[1]))
      }
      storageUnitTypesById = new Map(siteMap.storageUnitTypes.map((sut) => [sut.id, sut]))

      storageUnitRows = siteMap.storageUnitRows
      storageUnitRowsById = new Map(siteMap.storageUnitRows.map((sur) => [sur.id, sur]))

      unassignedStorageUnits = siteMap.unassignedStorageUnits
      updateMapSettings({ mapWidth: siteMap.width })
      mapHeight = siteMap.height
      mapBackgroundImageUrl = siteMap.backgroundImageUrl

      status = "ready"
    } catch (error) {
      status = "loadFailed"
      loadError = error.message
    }
  }

  let dragState
  let spacePressed = false
  let panState

  function handleDomainPointerDown(e) {
    if (e.detail.entityType !== "storageUnitRow") {
      panState = {
        startElementX: e.detail.elementX,
        startElementY: e.detail.elementY,
        startViewXCenter: viewXCenter,
        startViewYCenter: viewYCenter,
        startXScale: e.detail.xScale.copy(),
        startYScale: e.detail.yScale.copy(),
      }
    } else {
      if (e.detail.entityType == "storageUnitRow") {
        selectedStorageUnitRowId = e.detail.entityId
        const storageUnitRow = storageUnitRowsById.get(selectedStorageUnitRowId)

        dragState = {
          startX: e.detail.x,
          startY: e.detail.y,
          currentX: e.detail.x,
          currentY: e.detail.y,
          storageUnitRowId: e.detail.entityId,
          originalPosition: storageUnitRow.position,
        }
      } else {
        selectedStorageUnitRowId = null
      }
    }
  }

  async function handleDomainPointerUp(e) {
    panState = null

    if (dragState) {
      if (dragState.startX != dragState.currentX && dragState.startY != dragState.currentY) {
        try {
          const storageUnitRow = storageUnitRowsById.get(dragState.storageUnitRowId)

          const response = await fetch(
            `/company_admin/site_maps/${siteMapId}/site_map_storage_unit_rows/${dragState.storageUnitRowId}`,
            {
              method: "PATCH",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": document
                  .querySelector("meta[name='csrf-token']")
                  .getAttribute("content"),
              },
              body: JSON.stringify({
                position: storageUnitRow.position,
              }),
            }
          )
          if (!response.ok) {
            throw new Error("Failed to save storage unit row position")
          }
        } catch (error) {
          status = "saveFailed"
          loadError = error.message
        }
      }

      dragState = null
    } else {
      selectedStorageUnitRowId = null
    }
  }

  function handleDomainPointerMove(e) {
    if (panState) {
      const offsetX =
        panState.startXScale.invert(e.detail.elementX) -
        panState.startXScale.invert(panState.startElementX)
      const offsetY =
        panState.startYScale.invert(e.detail.elementY) -
        panState.startYScale.invert(panState.startElementY)

      viewXCenter = panState.startViewXCenter - offsetX
      viewYCenter = panState.startViewYCenter - offsetY
    } else if (dragState) {
      dragState.currentX = e.detail.x
      dragState.currentY = e.detail.y

      const storageUnitRow = storageUnitRowsById.get(dragState.storageUnitRowId)

      let x = dragState.originalPosition.x + (dragState.currentX - dragState.startX)

      let y = dragState.originalPosition.y + (dragState.currentY - dragState.startY)

      if ($mapSettings.isSnapToGridEnabled) {
        x = parseInt(x, 10)
        y = parseInt(y, 10)
      }

      storageUnitRow.position = { x, y }
      storageUnitRows = storageUnitRows
      storageUnitRowsById = storageUnitRowsById
    }
  }

  function zoomOut() {
    updateMapSettings({ viewWidth: $mapSettings.viewWidth * 1.25 })
    constrainView()
  }

  function zoomIn() {
    updateMapSettings({ viewWidth: $mapSettings.viewWidth * 0.8 })
    constrainView()
  }

  function zoomReset() {
    updateMapSettings({ viewWidth: 100 })
    constrainView()
  }

  function handleKeyDown(event) {
    if (event.key === " ") {
      event.preventDefault()
      spacePressed = true
    } else if (event.key === "=" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomIn()
    } else if (event.key === "-" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomOut()
    } else if (event.key === "0" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomReset()
    } else if (event.key === "d") {
      debugStore.enabled = !debugStore.enabled
    } else if (event.key === "g") {
      handleToggleSnapToGrid()
    } else if (event.key === "s") {
      const storageUnitRow = storageUnitRowsById.get(selectedStorageUnitRowId)

      if (storageUnitRow) {
        handleSaveStorageUnitRow({
          detail: { ...storageUnitRow, storageUnitRowId: storageUnitRow.id },
        })
      }
    }
  }

  function handleKeyUp(event) {
    if (event.key === " ") {
      event.preventDefault()
      spacePressed = false
    }
  }

  function constrainView() {
    const maxViewWidth = $mapSettings.mapWidth * 3

    updateMapSettings({
      viewWidth: Math.min(maxViewWidth, Math.max($mapSettings.viewWidth, minViewWidth)),
    })

    if (viewXCenter - $mapSettings.viewWidth / 2 < 0) {
      viewXCenter = $mapSettings.viewWidth / 2
    }

    if (viewXCenter + $mapSettings.viewWidth / 2 > $mapSettings.mapWidth) {
      viewXCenter = $mapSettings.mapWidth - $mapSettings.viewWidth / 2
    }
  }

  $: updateDebugStore({
    viewXCenter: viewXCenter.toFixed(4),
    viewYCenter: viewYCenter.toFixed(4),
    viewWidth: $mapSettings.viewWidth.toFixed(4),
  })

  async function handleCreateStorageUnitRow(e) {
    try {
      const response = await fetch(
        `/company_admin/site_maps/${siteMapId}/site_map_storage_unit_rows`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document
              .querySelector("meta[name='csrf-token']")
              .getAttribute("content"),
          },
          body: JSON.stringify({
            storage_unit_ids: e.detail.storageUnits.map((su) => su.id),
          }),
        }
      )
      if (response.ok) {
        load()
      } else {
        throw new Error("Failed to create storage unit row")
      }
    } catch (error) {
      status = "loadFailed"
      loadError = error.message
    }
  }

  async function handleSaveStorageUnitRow(e) {
    try {
      const { storageUnitRowId, align, position, rotation } = e.detail

      const response = await fetch(
        `/company_admin/site_maps/${siteMapId}/site_map_storage_unit_rows/${storageUnitRowId}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document
              .querySelector("meta[name='csrf-token']")
              .getAttribute("content"),
          },
          body: JSON.stringify({ align, position, rotation }),
        }
      )
      if (response.ok) {
        load()
      } else {
        throw new Error("Failed to save storage unit row")
      }
    } catch (error) {
      status = "loadFailed"
      loadError = error.message
    }
  }

  function handleToggleSnapToGrid() {
    mapSettings.update((current) => ({
      ...current,
      isSnapToGridEnabled: !current.isSnapToGridEnabled,
    }))
  }

  async function handleReorderStorageUnits(e) {
    try {
      const { storageUnitRowId, storageUnitIds } = e.detail

      const response = await fetch(
        `/company_admin/site_maps/${siteMapId}/site_map_storage_unit_rows/${storageUnitRowId}/reorder_storage_units`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document
              .querySelector("meta[name='csrf-token']")
              .getAttribute("content"),
          },
          body: JSON.stringify({ storage_unit_ids: storageUnitIds }),
        }
      )
      if (response.ok) {
        load()
      } else {
        throw new Error("Failed to reorder storage units")
      }
    } catch (error) {
      status = "loadFailed"
      loadError = error.message
    }
  }

  async function handleDeleteStorageUnitRow(e) {
    try {
      const response = await fetch(
        `/company_admin/site_maps/${siteMapId}/site_map_storage_unit_rows/${e.detail.storageUnitRowId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": document
              .querySelector("meta[name='csrf-token']")
              .getAttribute("content"),
          },
        }
      )
      if (response.ok) {
        selectedStorageUnitRowId = null
        load()
      } else {
        throw new Error("Failed to delete storage unit row")
      }
    } catch (error) {
      status = "loadFailed"
      loadError = error.message
    }
  }

  let submitButton = document.querySelector("[type=submit]")
  let buttonClasses = ""
  if (submitButton) {
    buttonClasses = [...submitButton.classList].join(" ")
  }
  const backURL =
    (document.querySelector("#back_link") && document.querySelector("#back_link").href) || "#"

  function handleWheel(event) {
    event.preventDefault()

    const multiplier = event.ctrlKey ? 2 : 1
    const maxViewWidth = $mapSettings.mapWidth * 5

    let scale = $mapSettings.viewWidth + throttleScrollDelta(event, 50) * multiplier
    scale = Math.min(maxViewWidth, Math.max(minViewWidth, scale))

    updateMapSettings({ viewWidth: scale })
  }
</script>

<svelte:window on:keydown={handleKeyDown} on:keyup={handleKeyUp} />

{#if status == "loading"}
  <p>Site map loading...</p>
{:else if status == "ready"}
  <div class="bg-gray-100 h-[100vh] w-full flex drop-shadow z-10">
    <div class="w-[250px] overflow-y-scroll p-4">
      <a class={buttonClasses + " mb-4 z-20"} href={backURL}> Back </a>

      {#if selectedStorageUnitRowId}
        <div>
          <StorageUnitRowPanel
            storageUnitRow={storageUnitRowsById.get(selectedStorageUnitRowId)}
            on:savestorageunitrow={handleSaveStorageUnitRow}
            on:deletestorageunitrow={handleDeleteStorageUnitRow}
            on:reorderstorageunits={handleReorderStorageUnits}
          />
        </div>
      {:else}
        <div class="absolute top-0 bottom-0 left-0 w-[250px]">
          <UnassignedStorageUnitsPanel
            {storageUnitTypesById}
            storageUnits={unassignedStorageUnits}
            on:createstorageunitrow={handleCreateStorageUnitRow}
          />
        </div>
      {/if}
    </div>

    {#if debugStore.enabled}
      <div class="rounded-lg p-3 bg-black/40 text-white absolute z-10 top-4 right-4 max-w-[300px]">
        {#each Object.entries(debugStore) as [key, value]}
          <div>{key}: {value}</div>
        {/each}
      </div>
    {/if}

    <div class="flex-1 overflow-hidden" on:wheel={handleWheel}>
      <MapView
        mapWidth={$mapSettings.mapWidth}
        on:domainpointerdown={handleDomainPointerDown}
        on:domainpointermove={handleDomainPointerMove}
        on:domainpointerup={handleDomainPointerUp}
        viewWidth={$mapSettings.viewWidth}
        {mapBackgroundImageUrl}
        {mapHeight}
        {selectedStorageUnitRowId}
        {spacePressed}
        {storageUnitRows}
        {storageUnitTypesById}
        {viewXCenter}
        {viewYCenter}
      />

      <div class="fixed top-4 right-4 bg-black/50 shadow-lg rounded-lg text-white overflow-clip">
        <div class="rounded-xl divide-y">
          <button
            on:click={(e) => {
              document
                .querySelector("#map-editor")
                .closest("dialog")
                .querySelector(".showModal")
                .click()
            }}
            class="hover:bg-black/20 cursor-pointer p-2 w-auto h-10 flex items-center gap-2 justify-center text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
              />
            </svg>
          </button>
          <button
            class="hover:bg-black/20 cursor-pointer p-2 w-auto h-10 flex items-center gap-2 justify-center text-sm"
            on:click={handleToggleSnapToGrid}
            title="Toggle Snap-to-Grid (Ctrl+g)"
          >
            {#if $mapSettings.isSnapToGridEnabled}
              <svg
                aria-hidden="true"
                class="w-6 h-6 text-white-500"
                fill="currentColor"
                id="puzzle-piece"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.25 5.337c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.036 1.007-1.875 2.25-1.875S15 2.34 15 3.375c0 .369-.128.713-.349 1.003-.215.283-.401.604-.401.959 0 .332.278.598.61.578 1.91-.114 3.79-.342 5.632-.676a.75.75 0 01.878.645 49.17 49.17 0 01.376 5.452.657.657 0 01-.66.664c-.354 0-.675-.186-.958-.401a1.647 1.647 0 00-1.003-.349c-1.035 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401.31 0 .557.262.534.571a48.774 48.774 0 01-.595 4.845.75.75 0 01-.61.61c-1.82.317-3.673.533-5.555.642a.58.58 0 01-.611-.581c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.035-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959a.641.641 0 01-.658.643 49.118 49.118 0 01-4.708-.36.75.75 0 01-.645-.878c.293-1.614.504-3.257.629-4.924A.53.53 0 005.337 15c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.036 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.369 0 .713.128 1.003.349.283.215.604.401.959.401a.656.656 0 00.659-.663 47.703 47.703 0 00-.31-4.82.75.75 0 01.83-.832c1.343.155 2.703.254 4.077.294a.64.64 0 00.657-.642z"
                />
              </svg>
            {:else}
              <svg
                aria-hidden="true"
                class="w-6 h-6 text-white-500"
                fill="none"
                id="puzzle-piece-o"
                stroke-width="1.5"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z"
                />
              </svg>
            {/if}
          </button>
        </div>
      </div>

      <div class="fixed bottom-4 right-4 bg-black/50 shadow-lg rounded-lg text-white overflow-clip">
        <div class="rounded-xl divide-y">
          <button
            on:click={(e) => {
              e.preventDefault()
              zoomIn()
            }}
            class="hover:bg-black/20 cursor-pointer p-4 w-10 h-10 flex items-center justify-center text-2xl"
            title="Zoom in (Ctrl++)"
          >
            +
          </button>
          <button
            aria-label="Reset zoom"
            on:click={(e) => {
              e.preventDefault()
              zoomReset()
            }}
            class="hover:bg-black/20 cursor-pointer w-10 h-10 flex items-center justify-center text-2xl"
            title="Reset zoom (Ctrl+0)"
          >
            <svg class="w-5 h-5 text-white-500">
              <svg
                id="arrow-path"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z"
                  clip-rule="evenodd"
                />
              </svg>
            </svg>
          </button>
          <button
            on:click={(e) => {
              e.preventDefault()
              zoomOut()
            }}
            class="hover:bg-black/20 cursor-pointer p-4 w-10 h-10 flex items-center justify-center text-2xl"
            title="Zoom out (Ctrl+-)"
          >
            -
          </button>
        </div>
      </div>
    </div>
  </div>
{:else}
  <p>Error: {loadError}</p>
{/if}
